import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, ShieldCheck, Smartphone, Trash2 } from "lucide-react";
import { useTRPC } from "@/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Loading from "@/components/Loading";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  getMfaStatus,
  removeMfaDevice,
  type MfaFactor,
  type SecondFactorType,
} from "../mfa";
import { AddDeviceDialog } from "./AddDeviceDialog";

const MFA_STATUS_QUERY_KEY = ["mfa", "status"];

const FACTOR_LABEL: Record<SecondFactorType, string> = {
  totp: "Authenticator app",
  webauthn: "Passkey",
};

const FactorIcon = ({ type }: { type: SecondFactorType }) =>
  type === "webauthn" ? (
    <KeyRound className="h-5 w-5 text-muted-foreground" />
  ) : (
    <Smartphone className="h-5 w-5 text-muted-foreground" />
  );

const formatAdded = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Self-contained 2FA device management card for settings pages. Generic across
 * org and provider settings — it fetches its own state and renders nothing for
 * users who neither require 2FA nor have any device enrolled, so it's safe to
 * drop in unconditionally.
 */
export const TwoFactorSettingsCard = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: user } = useQuery(trpc.user.getUser.queryOptions());
  const { data: status, isPending } = useQuery({
    queryKey: MFA_STATUS_QUERY_KEY,
    queryFn: getMfaStatus,
  });

  const [addOpen, setAddOpen] = useState(false);
  const [toRemove, setToRemove] = useState<MfaFactor | null>(null);
  const [removing, setRemoving] = useState(false);

  const mfaRequired = user?.mfa_required ?? false;
  const factors = status?.verifiedFactors ?? [];

  // Nothing to manage and 2FA isn't required → don't render at all.
  if (!isPending && !mfaRequired && factors.length === 0) {
    return null;
  }

  // Required users must keep at least one device; block removing the last one.
  const blockRemovingLast = mfaRequired && factors.length <= 1;

  const refetchStatus = () =>
    queryClient.invalidateQueries({ queryKey: MFA_STATUS_QUERY_KEY });

  const confirmRemove = async () => {
    if (!toRemove) {
      return;
    }
    setRemoving(true);
    try {
      await removeMfaDevice(toRemove.id);
      await refetchStatus();
      toast.success("Device removed");
      setToRemove(null);
    } catch (e) {
      toast.error(
        e instanceof Error && e.message ? e.message : "Couldn't remove device",
      );
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Two-factor authentication
            </CardTitle>
            <CardDescription>
              {mfaRequired
                ? "Your account requires a second factor. Manage the devices you use to sign in."
                : "Add a second factor to better protect your account."}
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => setAddOpen(true)}
            className="shrink-0 gap-1"
          >
            <Plus className="h-4 w-4" />
            Add device
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <div className="flex justify-center py-6">
            <Loading className="w-7 h-7" />
          </div>
        ) : factors.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No devices yet. Add an authenticator app or passkey to finish
            setting up two-factor authentication.
          </p>
        ) : (
          <ul className="divide-y">
            {factors.map((factor) => {
              const added = formatAdded(factor.createdAt);
              return (
                <li
                  key={factor.id}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <FactorIcon type={factor.factorType} />
                    <div>
                      <div className="font-medium">
                        {FACTOR_LABEL[factor.factorType]}
                      </div>
                      {added && (
                        <div className="text-xs text-muted-foreground">
                          Added {added}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={blockRemovingLast}
                    title={
                      blockRemovingLast
                        ? "Add another device before removing your last one"
                        : undefined
                    }
                    onClick={() => setToRemove(factor)}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Remove device</span>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}

        {blockRemovingLast && factors.length > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            This is your only second factor. Add another device before removing
            it.
          </p>
        )}
      </CardContent>

      <AddDeviceDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdded={() => {
          setAddOpen(false);
          void refetchStatus();
          toast.success("Device added");
        }}
      />

      <AlertDialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this device?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll no longer be able to use{" "}
              {toRemove ? FACTOR_LABEL[toRemove.factorType] : "this device"} to
              verify your identity when signing in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmRemove();
              }}
              disabled={removing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removing ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
